/**
 * The day/night cycle — issue #54, and it is **cosmetic**.
 *
 * That word is the whole specification, so it is worth spending a paragraph on
 * before any arithmetic. #54 posed the choice explicitly: a *mechanical* cycle
 * drops the surface biome's ambient at night, which makes torches matter above
 * ground and changes how the surface plays; a *cosmetic* one moves the sky and
 * nothing else. The second was chosen. So:
 *
 *   - Nothing here is imported by `src/sim/`, `src/world/` or `src/core/`
 *     except for the one call that turns a tick into a phase.
 *   - `LightTexture.ambient` — the term the terrain shader and every gameplay
 *     query share — is **not** touched. `world/lighting.ts::ambientAt` remains
 *     a function of depth alone.
 *   - What this module returns is a *recoloured backdrop palette*. Those five
 *     colours reach exactly one place: the sky half of `backdrop.ts`'s fragment
 *     shader. Ask the game "am I in the dark?" at midnight and at noon and you
 *     get the same answer, because nothing that answers that question can see
 *     any of this.
 *
 * If a future change routes any of this through `sim/light.ts`, it has stopped
 * being the feature that was asked for.
 *
 * ---------------------------------------------------------------------------
 * WHAT HAPPENS UNDERGROUND, WHICH IS WHERE MOST OF THIS GAME IS.
 *
 * Nothing. That is a decision, not an omission.
 *
 * A sun is meaningless in a sealed cave, and an ambient shift applied to a
 * place with no sky reads as a lighting glitch rather than as nightfall — you
 * would be standing in unchanging rock watching it dim for no visible cause.
 * So `tintPalette` recolours only the five *sky-side* entries of the palette
 * (`skyHigh`, `skyLow`, `silFar`, `silMid`, `silNear`) and returns the three
 * authored cave-wall depths (`far`, `mid`, `near`) **byte-identical**. Those
 * three are the only colours `caveWall()` in the shader reads.
 *
 * The fade is therefore free and already written: the shader blends the sky
 * branch into the cave branch over `BACKDROP_FADE_SPAN`, so the cycle's
 * influence goes to zero on exactly the same curve the sky itself does, and is
 * gone by 100 cells down. Descend at midnight and the darkness stops being
 * night and starts being depth, with no seam in between. `tests/dayNight.test.ts`
 * asserts the invariance of the three cave colours directly, at every phase,
 * for every biome — that assertion is the underground decision, written down
 * somewhere that fails if anyone changes their mind quietly.
 *
 * ---------------------------------------------------------------------------
 * NO WALL CLOCK. The phase is a function of the **simulation tick counter** and
 * nothing else, which is the project's central invariant (docs/01 §9,
 * `tests/sim.test.ts`, and the source scan in `tests/backdrop.test.ts` that
 * already forbade a clock in the backdrop). Same seed and same tick count give
 * the same sky, on any machine, at any frame rate, and a paused game has a
 * paused sky. The determinism tests in `tests/dayNight.test.ts` are the ones
 * that would catch a `Date.now` creeping in here.
 */

import type { BackdropPalette, Rgb } from './backdrop';

// ---------------------------------------------------------------- the clock

/**
 * One full cycle, in simulation ticks. 43200 at the 60 Hz step `src/core/game.ts`
 * runs — **twelve minutes** of unpaused play, dawn to dawn.
 *
 * Chosen against two failure modes rather than for a number, and the first
 * candidate (eight minutes) was rejected by measurement rather than by taste.
 *
 * Too fast and the background is a background that *moves*, which is the one
 * thing #54 and the original backdrop work both say it must not be. The number
 * that decides this is the steepest one-second change in `daylight` as a
 * fraction of the whole swing: at eight minutes it is **4.1 %**, at twelve it
 * is **1.8 %**, and the transition it belongs to lasts 82 s rather than 37 s.
 * Thirty-seven seconds from full day to full night is a sunset *animation* —
 * something happening on screen — where eighty is weather.
 *
 * Too slow and the cycle is a feature nobody ever observes. A surface excursion
 * is two or three minutes, which is a fifth to a quarter of a twelve-minute day
 * — enough that the sky is visibly not where you left it. A thirty-minute cycle
 * would show most players one static sky per run, which is the
 * authored-and-unreachable shape docs/13 §3 is about.
 *
 * The 60 in the arithmetic above is `SIM_HZ` in `src/core/game.ts`, which this
 * module cannot import (it would be a render → core edge, and that file is not
 * ours). `tests/dayNight.test.ts` scans it instead, so the "twelve minutes"
 * claim here breaks the build rather than going quietly stale if the simulation
 * rate ever moves.
 */
export const DAY_LENGTH_TICKS = 43200;

/**
 * Phase at tick 0 — mid-morning, sun well up.
 *
 * A run that opens at midnight is a run whose first impression is that the
 * renderer is broken. 0.34 is inside the flat middle of the day, so the opening
 * sky is unambiguously daylight and is not already sliding; dusk begins about
 * four and a half minutes of unpaused play later, which is roughly a first
 * surface excursion plus the walk back.
 */
export const DAY_START_PHASE = 0.34;

/**
 * The phase at which `tintPalette` is exactly the identity. See its comment;
 * this is also the default the backdrop draws with when nothing supplies a
 * phase, so an unwired build renders bit-for-bit what it rendered before this
 * file existed.
 */
export const NOON_PHASE = 0.5;

/**
 * Simulation tick → cycle phase in [0, 1).
 *
 * 0 is midnight, 0.25 sunrise, 0.5 noon, 0.75 sunset. Ticks, not seconds and
 * not frames: `cells.tick` only advances inside `stepSimulation`, so the sky
 * holds still while the game is paused and advances at the same rate on a
 * 30 Hz machine as on a 144 Hz one.
 */
export function dayPhase(tick: number): number {
  const t = tick / DAY_LENGTH_TICKS + DAY_START_PHASE;
  const f = t - Math.floor(t);
  // `t` can be -0 or land exactly on 1 after rounding; normalise both.
  return f < 0 || f >= 1 ? 0 : f;
}

/**
 * Height of the sun, -1 (midnight) to +1 (noon), 0 at the two horizons.
 *
 * A cosine rather than a triangle wave because the two things that want to be
 * smooth — the dawn ramp and the dusk ramp — are the only parts of the cycle
 * anybody watches, and a triangle wave has its corners exactly there.
 */
export function sunElevation(phase: number): number {
  return -Math.cos(2 * Math.PI * phase);
}

function smoothstep(a: number, b: number, x: number): number {
  const t = (x - a) / (b - a);
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  return t * t * (3 - 2 * t);
}

/**
 * How much daylight there is, 0 (full night) to 1 (full day).
 *
 * The window is deliberately asymmetric about the horizon: `smoothstep(-0.30,
 * 0.42, elevation)` reaches full day while the sun is still well short of the
 * top of its arc, and full night while it is still well short of the bottom, so
 * the middles of day and night are *flat* and the whole of the visible change
 * is packed into the two twilights. A sinusoidal brightness would spend the
 * entire cycle drifting, which is precisely the moving background the backdrop
 * was written not to be.
 *
 * The consequence, measured over 4096 phases: the sky is pinned at its noon
 * value for **37 %** of the cycle and at its midnight value for **40 %**, so
 * over three quarters of a day is a sky that is simply not changing at all, and
 * the remaining quarter is two 82-second ramps.
 */
export function daylight(phase: number): number {
  return smoothstep(-0.3, 0.42, sunElevation(phase));
}

/**
 * Dawn/dusk warmth, 0 away from the horizons and 1 crossing them.
 *
 * Peaks where `sunElevation` is 0, which is where a real sky is reddest,
 * and is zero over most of the cycle so the warm mix is not a permanent
 * sepia cast on the whole game.
 */
export function twilight(phase: number): number {
  const e = Math.abs(sunElevation(phase));
  return smoothstep(1, 0, e / 0.42);
}

/** Convenience: 1 - daylight. The star field and the night mixes key off it. */
export function nightness(phase: number): number {
  return 1 - daylight(phase);
}

// --------------------------------------------------------------- the colours

/**
 * The two colours the sky is pulled toward, and the only invented hues here.
 *
 * Everything else in `backdropPalette` derives from a biome's own authored
 * triple, and these two do not fight that: they are *mix targets*, applied at
 * fractions below 0.75, so the Cinder March's midnight is still redder than the
 * Rotwood's. A night that replaced the palette rather than pulling on it would
 * make twelve authored backdrops into one, which is the defect
 * `tests/backdrop.test.ts` already names for the daytime case and which
 * `tests/dayNight.test.ts` now re-checks at midnight.
 */
const NIGHT_SKY: Rgb = { r: 0.045, g: 0.058, b: 0.105 };
const DUSK: Rgb = { r: 0.62, g: 0.28, b: 0.14 };

/** Multiplier on the sky at full night, before the mix toward `NIGHT_SKY`. */
const NIGHT_SKY_DIM = 0.34;
/**
 * The same for the silhouettes, and it is lower on purpose: after dark a
 * treeline goes to near-black faster than the sky it stands against does, which
 * is what keeps the horizon readable at midnight instead of letting the whole
 * screen converge on one grey.
 *
 * 0.20 rather than the 0.26 this was first written with, and the difference was
 * decided by looking at a rendered midnight rather than by argument. The three
 * silhouettes are separated by *haze*, which is a fraction of the gap between
 * the near colour and the horizon — so the darker the near colour, the wider
 * the whole ramp opens. At 0.26, with a night-compressed haze, Rotwood Reach's
 * three layers came out at luminance **9.6, 8.5, 7.4** of 255: a byte apart, a
 * ramp that passes every ordering test and is not a depth cue, and a frame that
 * showed hills and then a black mass where the treeline should be. At 0.20 with
 * full haze they are **12.5, 9.4, 5.6** — a 2.2x spread, against the noon
 * palette's 2.0x at 56, 42, 28. Night is darker; it is not flatter.
 */
const NIGHT_SIL_DIM = 0.2;

/** How far the zenith and the horizon are pulled toward `NIGHT_SKY` at midnight. */
const NIGHT_MIX_HIGH = 0.72;
const NIGHT_MIX_LOW = 0.46;
/** And toward `DUSK` at the peak of twilight. The horizon gets most of it. */
const DUSK_MIX_HIGH = 0.1;
const DUSK_MIX_LOW = 0.34;

function mix(a: Rgb, b: Rgb, t: number): Rgb {
  return {
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
  };
}

function dim(c: Rgb, k: number): Rgb {
  return { r: c.r * k, g: c.g * k, b: c.b * k };
}

/**
 * Recolour a backdrop palette for a point in the cycle.
 *
 * **Exactly the identity at `NOON_PHASE`** — every coefficient below is written
 * so that `daylight = 1, twilight = 0` reduces it to the input. That is not a
 * tidiness point: it means this whole module is a pure addition to a shipped
 * look rather than a re-tuning of it, `tests/backdrop.test.ts`'s existing
 * palette assertions remain assertions about the noon sky, and a build that
 * never wires up a phase draws what it always drew.
 *
 * The three cave-wall depths are returned untouched. See the header.
 */
export function tintPalette(p: BackdropPalette, phase: number): BackdropPalette {
  const d = daylight(phase);
  const tw = twilight(phase);
  const nz = 1 - d;

  const kSky = NIGHT_SKY_DIM + (1 - NIGHT_SKY_DIM) * d;
  const kSil = NIGHT_SIL_DIM + (1 - NIGHT_SIL_DIM) * d;

  const skyHigh = mix(
    mix(dim(p.skyHigh, kSky), NIGHT_SKY, nz * NIGHT_MIX_HIGH),
    DUSK,
    tw * DUSK_MIX_HIGH,
  );
  const skyLow = mix(
    mix(dim(p.skyLow, kSky), NIGHT_SKY, nz * NIGHT_MIX_LOW),
    DUSK,
    tw * DUSK_MIX_LOW,
  );

  // The silhouette chain is *rebuilt* rather than tinted: one darkened near
  // colour, then the same 0.15 and 0.30 haze fractions `backdropPalette` used,
  // against the sky that is now actually behind it. Two things follow. At
  // `daylight = 1` these are literally `backdropPalette`'s own two lines, so
  // noon is the identity; and at any other phase the ordering is structural
  // rather than lucky, because all three are on one segment from `silNear` to
  // `skyLow` and cannot cross.
  //
  // The haze fractions themselves do *not* shrink at night, which was the first
  // thing tried. Compressing the ramp after dark is defensible physics and it
  // rendered as hills-then-a-black-mass: see `NIGHT_SIL_DIM`.
  const silNear = dim(p.silNear, kSil);
  return {
    far: p.far,
    mid: p.mid,
    near: p.near,
    skyHigh,
    skyLow,
    silNear,
    silMid: mix(silNear, skyLow, 0.15),
    silFar: mix(silNear, skyLow, 0.3),
  };
}

// ------------------------------------------------------------------- stars

/**
 * Fraction of sky cells that hold a star. One in 625.
 *
 * At the 480x270 viewport that is about 200 points over a full screen of sky,
 * before the horizon fade takes the lower ones out — a field, not a scatter of
 * six, and not the milky way either.
 */
export const STAR_DENSITY = 0.0016;

/** Faintly blue, and never full white: a white pixel out-reads the foreground. */
export const STAR_TINT: Rgb = { r: 0.62, g: 0.66, b: 0.82 };

/**
 * Brightness of the star at a far-layer cell, 0 where there is none.
 *
 * One hash answers both questions. A cell is a star when its hash lands in the
 * top `STAR_DENSITY` of the range, and *where* in that tail it lands is the
 * magnitude — so the field has a few bright points and many faint ones for the
 * cost of the test that found it, and adding a second hash for magnitude would
 * have bought nothing.
 *
 * Quantised to whole cells for the same reason the near cave speckle is
 * (`backdrop.ts`, `caveWall`): an unquantised point sample crawls against the
 * pixel grid as the camera moves and reads as shimmer. This sits on the *far*
 * layer, at parallax 0.10, so the field is nearly welded to the screen — which
 * is what a star field a very long way away should do.
 *
 * Mirrored line-for-line by `starMask` in the fragment shader.
 */
export function starMask(qx: number, qy: number, seed: number): number {
  const p = hash2(Math.floor(qx), Math.floor(qy), seed ^ 0x7c);
  const t = (p - (1 - STAR_DENSITY)) / STAR_DENSITY;
  if (t <= 0) return 0;
  return 0.2 + 0.8 * t * t;
}

/** `hash2f` from core/rng.ts, restated so the GLSL transcription is adjacent. */
function hash2(x: number, y: number, seed: number): number {
  let h = (Math.imul(x | 0, 0x27d4eb2d) ^ Math.imul(y | 0, 0x165667b1) ^ seed) >>> 0;
  h = (h ^ (h >>> 15)) >>> 0;
  h = Math.imul(h, 0x2c1b3c6d) >>> 0;
  h = (h ^ (h >>> 12)) >>> 0;
  h = Math.imul(h, 0x297a2d39) >>> 0;
  h = (h ^ (h >>> 15)) >>> 0;
  return h / 4294967296;
}
